import PolicyModal from "./components/PolicyModal.vue";

require('./bootstrap');

import { createApp } from 'vue';
import { createStore } from 'vuex';
import 'es6-promise/auto'
import 'promise-polyfill/src/polyfill';
import mitt from 'mitt';

import AlIntakePlugin from '@empoweredlegal/al-intake-plugin';
import '@empoweredlegal/al-intake-plugin/style.css';

// Import store modules
import LeadStore from './components/store/lead.js';
import MetaStore from './components/store/meta.js';

// Import critical components
import NavigationMain from './components/LeadCapture/NavigationMain.vue';
import IntakePluginCapture from './components/IntakeCapture/IntakePluginCapture.vue';
import GenericModal from "./components/GenericModal.vue";
import CallModal from './components/CallModal.vue';

// Sync-loaded to avoid production chunk-loading errors (see app.js component registrations below)
import ResponsivePlayer from './components/ResponsivePlayer.vue';
import Faq from './components/Faq.vue';
import ConversionReporting from './components/LeadCapture/ConversionReporting.vue';
import HomepageFaq from './components/HomepageFaq.vue';
import HomepageMenu from './components/HomepageMenu.vue';
import DirectConnect from './components/DirectConnect.vue';
import DirectoryBrowser from './components/Directory/DirectoryBrowser.vue';
import ModalBase from './components/ModalBase.vue';
import Dots from './components/Dots.vue';
import LeadAnswered from './components/LeadAnswered.vue';
import LeadBusy from './components/LeadBusy.vue';
import LeadCanceled from './components/LeadCanceled.vue';
import LeadChoice from './components/LeadChoice.vue';
import LeadChoiceMini from './components/LeadChoiceMini.vue';
import LeadCompleted from './components/LeadCompleted.vue';
import LeadCompletedEarly from './components/LeadCompletedEarly.vue';
import LeadFailed from './components/LeadFailed.vue';
import LeadInitiated from './components/LeadInitiated.vue';
import LeadNoAnswer from './components/LeadNoAnswer.vue';
import LeadRinging from './components/LeadRinging.vue';
import TenantAnswered from './components/TenantAnswered.vue';
import TenantBusy from './components/TenantBusy.vue';
import TenantCanceled from './components/TenantCanceled.vue';
import TenantFailed from './components/TenantFailed.vue';
import TenantInitiated from './components/TenantInitiated.vue';
import TenantNoAnswer from './components/TenantNoAnswer.vue';
import TenantRinging from './components/TenantRinging.vue';
import ScheduleAppointment from './components/ScheduleAppointment.vue';
import CalendarCard from './components/CalendarCard.vue';
import ContactCard from './components/ContactCard.vue';
import FormCard from './components/FormCard.vue';
import CalendlyScheduled from './components/CalendlyScheduled.vue';
import CallError from './components/CallError.vue';
import CallScheduled from './components/CallScheduled.vue';
import SmsModal from './components/SmsModal.vue';

// Create Vuex store
const store = createStore({
    state() {
        return {};
    },
    mutations: {},
    actions: {},
    modules: {
        lead: LeadStore,
        meta: MetaStore
    }
});

// Create Vue app
const app = createApp({
    data() {
        return {
            showCityOverlay: true
        }
    }
});

// Vue setup
// setting axios as a global property (needed from the typeahead component)
app.config.globalProperties.$http = axios;

// vue global event emitter/listener - using mitt with Vue 2 API compatibility
const baseEmitter = mitt();

// Create Vue 2 compatible wrapper
const emitter = {
    // Vue 2 style methods
    $on: baseEmitter.on.bind(baseEmitter),
    $off: baseEmitter.off.bind(baseEmitter),
    $emit: baseEmitter.emit.bind(baseEmitter),
    // Vue 3 / mitt style methods (for compatibility)
    on: baseEmitter.on.bind(baseEmitter),
    off: baseEmitter.off.bind(baseEmitter),
    emit: baseEmitter.emit.bind(baseEmitter)
};

app.config.globalProperties.$event = emitter;

app.use(AlIntakePlugin);

// Explicitly register critical components
app.component('navigation-main', NavigationMain);
app.component('intake-plugin-capture', IntakePluginCapture);
app.component('policy-modal', PolicyModal);
app.component('generic-modal', GenericModal);

// Register all components synchronously to avoid production chunk-loading errors
app.component('responsive-player', ResponsivePlayer);
app.component('faq', Faq);
app.component('conversion-reporting', ConversionReporting);
app.component('homepage-faq', HomepageFaq);
app.component('homepage-menu', HomepageMenu);

app.component('direct-connect', DirectConnect);
app.component('directory-browser', DirectoryBrowser);
app.component('call-modal', CallModal);
app.component('modal-base', ModalBase);
app.component('dots', Dots);
app.component('lead-choice-mini', LeadChoiceMini);

// Lead States
app.component('lead-answered', LeadAnswered);
app.component('lead-busy', LeadBusy);
app.component('lead-canceled', LeadCanceled);
app.component('lead-choice', LeadChoice);
app.component('lead-completed', LeadCompleted);
app.component('lead-completed-early', LeadCompletedEarly);
app.component('lead-failed', LeadFailed);
app.component('lead-initiated', LeadInitiated);
app.component('lead-no-answer', LeadNoAnswer);
app.component('lead-ringing', LeadRinging);

// Tenant States
app.component('tenant-answered', TenantAnswered);
app.component('tenant-busy', TenantBusy);
app.component('tenant-canceled', TenantCanceled);
app.component('tenant-failed', TenantFailed);
app.component('tenant-initiated', TenantInitiated);
app.component('tenant-no-answer', TenantNoAnswer);
app.component('tenant-ringing', TenantRinging);

// Cards & Extra Modals
app.component('schedule-appointment', ScheduleAppointment);
app.component('calendar-card', CalendarCard);
app.component('contact-card', ContactCard);
app.component('form-card', FormCard);
app.component('calendly-scheduled', CalendlyScheduled);
app.component('call-error', CallError);
app.component('call-scheduled', CallScheduled);

app.component('sms-modal', SmsModal);


window.isTrustpilotWidgetEnabled = false;

// Swiper (Dynamic Import)
const loadSwiper = async () => {
    if (document.querySelector('.testimonial-slider')) {
        const [SwiperModule, NavigationModule] = await Promise.all([
            import('swiper'),
            import('swiper/modules')
        ]);
        window.Swiper = SwiperModule.default;
        window.SwiperNav = NavigationModule.Navigation;
        document.dispatchEvent(new CustomEvent('swiper:ready'));
    }
};
loadSwiper();

// Use the store and mount the app
app.use(store);
window.app = app.mount('#app');

function toggleVisibility(element, targetId, toggleClass) {
    element.addEventListener('click', (event) => {
        document.getElementById(targetId).classList.toggle(toggleClass);
        event.target.classList.toggle(toggleClass);
    });
}

const readMore = document.querySelector('p[data-expand="read-more"]');
if (readMore) {
    toggleVisibility(readMore, 'extra-info', 'hidden');
}

const readLess = document.querySelector('p[data-expand="read-less"]');
if (readLess) {
    readLess.addEventListener('click', () => {
        const extraInfo = document.getElementById('extra-info');
        extraInfo.classList.toggle('hidden');
        document.getElementById('sector').scrollIntoView();
        if (readMore) {
            readMore.classList.remove('hidden');
        }
    });
}

window.getCurrentStep = function () {
    let url = new URL(window.location.href);
    return url.searchParams.get('step') || '';
}
